import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ShoppingCart, ChevronLeft, Check, RefreshCw, Package, ChevronRight,
  Gift, Heart, Plane, ThumbsUp, Smile, Trophy, Baby, Flower2, X, MessageSquare, CreditCard,
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const HAMPER_IMAGES = {
  'smallPVC-hamper':       '/images/products/smallPVC-hamper.jpg',
  'kraftbox-hamper':       '/images/products/kraftbox-hamper.jpg',
  'base8-hamper':          '/images/products/base8-hamper.jpg',
  'base10-hamper':         '/images/products/base10-hamper.jpg',
  'pvc-medium-hamper':     '/images/products/pvc-medium-hamper.jpg',
  'corrugated-box-hamper': '/images/products/corrugated-box-hamper.jpg',
};
const DEFAULT_IMG = 'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const PLACEHOLDER_IMG = 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400';

const GREETING_CARD_OPTIONS = [
  { id: 'birthday',        name: 'Birthday',        Icon: Gift },
  { id: 'anniversary',     name: 'Anniversary',     Icon: Heart },
  { id: 'wedding',         name: 'Wedding',         Icon: Heart },
  { id: 'baby-shower',     name: 'Baby Shower',     Icon: Baby },
  { id: 'get-well-soon',   name: 'Get Well Soon',   Icon: Flower2 },
  { id: 'thank-you',       name: 'Thank You',       Icon: ThumbsUp },
  { id: 'congratulations', name: 'Congrats',        Icon: Trophy },
  { id: 'farewell',        name: 'Farewell',        Icon: Plane },
  { id: 'no-card',         name: 'No Card',         Icon: X },
];

// ── Slot bar ────────────────────────────────────────────────────────────────
function SlotBar({ itemGroups, selections, activeSlotIdx, onSlotClick, greetingCard }) {
  const totalSlots = itemGroups.length + 1; // +1 for greeting card
  const greetingSlotIdx = itemGroups.length;
  const greetingFilled = greetingCard.occasion !== null;
  const greetingOccasion = GREETING_CARD_OPTIONS.find(o => o.id === greetingCard.occasion);

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="slot-bar">
      {/* Product item slots */}
      {itemGroups.map((group, idx) => {
        const selectedOptId = selections[group.id];
        const selOpt = group.options.find(o => o.id === selectedOptId);
        const isActive = idx === activeSlotIdx;
        const isFilled = !!selOpt;
        return (
          <button
            key={group.id}
            onClick={() => onSlotClick(idx)}
            data-testid={`slot-${idx}`}
            className={`flex-shrink-0 flex flex-col items-center justify-center rounded-xl border-2 transition-all p-2 min-w-[72px] max-w-[90px]
              ${isActive ? 'border-primary bg-amber-50 shadow-md' : isFilled ? 'border-amber-300 bg-amber-50/60' : 'border-dashed border-gray-300 bg-gray-50'}`}
          >
            {isFilled ? (
              <>
                <div className="h-10 w-10 rounded-lg overflow-hidden mb-1 flex-shrink-0">
                  <img src={selOpt.image || PLACEHOLDER_IMG} alt={selOpt.name}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.src = PLACEHOLDER_IMG; }} />
                </div>
                <span className="text-[10px] font-medium text-accent text-center leading-tight line-clamp-2">{selOpt.name}</span>
                {isActive && <div className="mt-1 h-1 w-1 rounded-full bg-primary" />}
              </>
            ) : (
              <>
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-1 ${isActive ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <Package className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-gray-400'}`} />
                </div>
                <span className={`text-[10px] text-center leading-tight ${isActive ? 'text-primary font-semibold' : 'text-gray-400'}`}>Item {idx + 1}</span>
                {isActive && <div className="mt-1 h-1 w-1 rounded-full bg-primary" />}
              </>
            )}
          </button>
        );
      })}

      {/* Greeting card slot (always last) */}
      <button
        onClick={() => onSlotClick(greetingSlotIdx)}
        data-testid="slot-greeting-card"
        className={`flex-shrink-0 flex flex-col items-center justify-center rounded-xl border-2 transition-all p-2 min-w-[72px] max-w-[90px]
          ${activeSlotIdx === greetingSlotIdx
            ? 'border-primary bg-amber-50 shadow-md'
            : greetingFilled
              ? 'border-rose-300 bg-rose-50/60'
              : 'border-dashed border-gray-300 bg-gray-50'}`}
      >
        {greetingFilled && greetingOccasion ? (
          <>
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-1 ${greetingCard.occasion === 'no-card' ? 'bg-gray-100' : 'bg-rose-100'}`}>
              <greetingOccasion.Icon className={`h-5 w-5 ${greetingCard.occasion === 'no-card' ? 'text-gray-400' : 'text-rose-500'}`} />
            </div>
            <span className={`text-[10px] font-medium text-center leading-tight line-clamp-2 ${greetingCard.occasion === 'no-card' ? 'text-gray-400' : 'text-rose-600'}`}>
              {greetingCard.occasion === 'no-card' ? 'No Card' : greetingOccasion.name}
            </span>
            {activeSlotIdx === greetingSlotIdx && <div className="mt-1 h-1 w-1 rounded-full bg-primary" />}
          </>
        ) : (
          <>
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-1 ${activeSlotIdx === greetingSlotIdx ? 'bg-amber-100' : 'bg-gray-100'}`}>
              <CreditCard className={`h-5 w-5 ${activeSlotIdx === greetingSlotIdx ? 'text-primary' : 'text-gray-400'}`} />
            </div>
            <span className={`text-[10px] text-center leading-tight ${activeSlotIdx === greetingSlotIdx ? 'text-primary font-semibold' : 'text-gray-400'}`}>
              Add Card
            </span>
            {activeSlotIdx === greetingSlotIdx && <div className="mt-1 h-1 w-1 rounded-full bg-primary" />}
          </>
        )}
      </button>
    </div>
  );
}

// ── Item card (product) ──────────────────────────────────────────────────────
function ItemCard({ option, isSelected, onSelect }) {
  const price = option.display_price ?? option.addon_price ?? 0;
  return (
    <button
      onClick={onSelect}
      data-testid={`item-card-${option.id}`}
      className={`group relative flex flex-col rounded-xl border-2 overflow-hidden text-left transition-all duration-200
        ${isSelected ? 'border-primary shadow-md ring-2 ring-primary/20' : 'border-amber-100 hover:border-amber-300 hover:shadow-sm'}`}
    >
      <div className="relative h-24 w-full overflow-hidden bg-amber-50">
        <img src={option.image || PLACEHOLDER_IMG} alt={option.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={e => { e.target.src = PLACEHOLDER_IMG; }} />
        {isSelected && (
          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
            <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <Check className="h-4 w-4 text-white" />
            </div>
          </div>
        )}
      </div>
      <div className="px-2 py-2">
        <p className={`text-xs font-semibold leading-tight line-clamp-2 ${isSelected ? 'text-primary' : 'text-accent'}`}>{option.name}</p>
        <p className={`text-xs mt-0.5 font-medium ${price > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
          {price > 0 ? `+₹${price}` : 'Included'}
        </p>
      </div>
    </button>
  );
}

// ── Greeting card occasion card ──────────────────────────────────────────────
function OccasionCard({ option, isSelected, onSelect }) {
  const { Icon } = option;
  const isNoCard = option.id === 'no-card';
  return (
    <button
      onClick={onSelect}
      data-testid={`occasion-card-${option.id}`}
      className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 gap-2
        ${isSelected
          ? isNoCard ? 'border-gray-400 bg-gray-50' : 'border-rose-400 bg-rose-50 ring-2 ring-rose-200'
          : 'border-amber-100 hover:border-rose-200 hover:bg-rose-50/40'}`}
    >
      <div className={`h-9 w-9 rounded-full flex items-center justify-center
        ${isSelected ? (isNoCard ? 'bg-gray-200' : 'bg-rose-100') : 'bg-amber-50'}`}>
        <Icon className={`h-4 w-4 ${isSelected ? (isNoCard ? 'text-gray-500' : 'text-rose-500') : 'text-amber-600'}`} />
      </div>
      <span className={`text-xs font-medium leading-tight text-center ${isSelected ? (isNoCard ? 'text-gray-500' : 'text-rose-600') : 'text-accent'}`}>
        {option.name}
      </span>
      {isSelected && !isNoCard && <Check className="h-3 w-3 text-rose-500" />}
    </button>
  );
}

// ── Main Dialog ──────────────────────────────────────────────────────────────
export const HamperOptionsDialog = ({ hamper, open, onClose, editMode = false, hamperCartId = null, initialState = null }) => {
  const [step, setStep] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [itemSelections, setItemSelections] = useState({});
  const [activeSlotIdx, setActiveSlotIdx] = useState(0);
  const [greetingCard, setGreetingCard] = useState({ occasion: null, message: '' });
  const [saving, setSaving] = useState(false);

  const { addHamperToCart, updateHamperInCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      if (editMode && initialState) {
        setStep(initialState.step || 2);
        setSelectedVariant(initialState.variant || null);
        setItemSelections(initialState.selections || {});
        setActiveSlotIdx(0);
        setGreetingCard(initialState.greetingCard || { occasion: null, message: '' });
      } else {
        setStep(1);
        setSelectedVariant(null);
        setItemSelections({});
        setActiveSlotIdx(0);
        setGreetingCard({ occasion: null, message: '' });
      }
    }
  }, [open, hamperCartId]);

  const variants = hamper?.selection_groups?.[0]?.options ?? [];
  const itemGroups = selectedVariant?.selection_groups ?? [];
  const greetingSlotIdx = itemGroups.length;

  const totalPrice = useMemo(() => {
    if (!hamper) return 0;
    let price = hamper.base_price;
    for (const group of itemGroups) {
      const sel = itemSelections[group.id];
      if (sel) {
        const opt = group.options.find(o => o.id === sel);
        if (opt) price += (opt.display_price ?? opt.addon_price ?? 0);
      }
    }
    return price;
  }, [hamper, itemGroups, itemSelections]);

  const allItemsSelected = itemGroups.every(g => itemSelections[g.id]) && greetingCard.occasion !== null;
  const filledProductCount = itemGroups.filter(g => itemSelections[g.id]).length;
  const totalSlots = itemGroups.length + 1;
  const filledCount = filledProductCount + (greetingCard.occasion !== null ? 1 : 0);

  const handleSelectVariant = (variant) => {
    setSelectedVariant(variant);
    setItemSelections({});
    setActiveSlotIdx(0);
    setGreetingCard({ occasion: null, message: '' });
    setStep(2);
  };

  const handleItemSelect = (groupId, optionId) => {
    const newSelections = { ...itemSelections, [groupId]: optionId };
    setItemSelections(newSelections);
    // Auto-advance to next empty product slot, then greeting card slot
    const nextProductEmpty = itemGroups.findIndex((g, i) => i > activeSlotIdx && !newSelections[g.id]);
    if (nextProductEmpty !== -1) {
      setActiveSlotIdx(nextProductEmpty);
    } else {
      // All product slots filled? Check if we should advance to greeting card slot
      const allProductFilled = itemGroups.every(g => newSelections[g.id]);
      if (allProductFilled && greetingCard.occasion === null) {
        setActiveSlotIdx(greetingSlotIdx);
      } else {
        const anyProductEmpty = itemGroups.findIndex(g => !newSelections[g.id]);
        if (anyProductEmpty !== -1) setActiveSlotIdx(anyProductEmpty);
      }
    }
  };

  const handleOccasionSelect = (occasionId) => {
    setGreetingCard(prev => ({
      ...prev,
      occasion: occasionId,
      message: occasionId === 'no-card' ? '' : prev.message,
    }));
  };

  const buildSelectionDetails = () => {
    const details = [];
    if (selectedVariant) {
      details.push({
        group_id: hamper.selection_groups[0].id,
        group_label: hamper.selection_groups[0].label,
        option_id: selectedVariant.id,
        option_name: selectedVariant.name,
        addon_price: selectedVariant.addon_price,
      });
    }
    for (const group of itemGroups) {
      const optId = itemSelections[group.id];
      if (optId) {
        const opt = group.options.find(o => o.id === optId);
        if (opt) {
          details.push({
            group_id: group.id,
            group_label: group.label,
            option_id: opt.id,
            option_name: opt.name,
            addon_price: opt.display_price ?? opt.addon_price ?? 0,
          });
        }
      }
    }
    // Greeting card
    if (greetingCard.occasion && greetingCard.occasion !== 'no-card') {
      const occ = GREETING_CARD_OPTIONS.find(o => o.id === greetingCard.occasion);
      details.push({
        group_id: 'greeting-card',
        group_label: 'Greeting Card',
        option_id: greetingCard.occasion,
        option_name: occ?.name ?? greetingCard.occasion,
        addon_price: 0,
        message: greetingCard.message,
      });
    }
    return details;
  };

  const handleSubmit = async () => {
    if (!user) { navigate('/login'); return; }
    if (!selectedVariant) { toast.error('Please select a variant'); return; }
    if (!allItemsSelected) { toast.error('Please complete all selections including the greeting card'); return; }
    setSaving(true);
    try {
      const payload = {
        hamper_id: hamper.id,
        hamper_name: `${hamper.name} — ${selectedVariant.name}`,
        selections: buildSelectionDetails(),
        unit_price: totalPrice,
        quantity: 1,
      };
      if (editMode && hamperCartId) {
        await updateHamperInCart(hamperCartId, payload);
        toast.success('Hamper updated!');
      } else {
        await addHamperToCart(payload);
        toast.success(`${hamper.name} added to cart!`);
      }
      onClose();
    } catch {
      toast.error(editMode ? 'Failed to update hamper.' : 'Failed to add to cart.');
    } finally {
      setSaving(false);
    }
  };

  if (!hamper) return null;

  const img = HAMPER_IMAGES[hamper.id] || DEFAULT_IMG;
  const isGreetingSlotActive = activeSlotIdx === greetingSlotIdx;
  const activeGroup = !isGreetingSlotActive ? itemGroups[activeSlotIdx] : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="hamper-options-dialog">
        <DialogTitle className="sr-only">{hamper.name}</DialogTitle>

        {/* Hero */}
        <div className="relative h-40 sm:h-48 overflow-hidden rounded-t-lg flex-shrink-0">
          <img src={img} alt={hamper.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-4 text-white">
            <h2 className="text-xl font-bold">{hamper.name}</h2>
            {selectedVariant && step === 2 && (
              <p className="text-sm text-white/80 mt-0.5">{selectedVariant.name}</p>
            )}
          </div>
          <div className="absolute top-3 left-3 flex gap-1.5">
            <Badge className="bg-primary text-white border-0 text-sm">Starts at ₹{hamper.base_price}</Badge>
            {editMode && <Badge className="bg-amber-500 text-white border-0 text-xs">Editing</Badge>}
          </div>
          <div className="absolute bottom-4 right-4 flex gap-1.5">
            {[1, 2].map(s => (
              <div key={s} className={`h-2 w-2 rounded-full transition-all ${step >= s ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {/* ── STEP 1: Pick Variant ── */}
          {step === 1 && (
            <div data-testid="step-variant-selection">
              <h3 className="font-semibold text-accent mb-1">{hamper.selection_groups[0]?.label ?? 'Select Variant'}</h3>
              <p className="text-xs text-muted-foreground mb-4">Choose the contents of your hamper</p>
              <div className="space-y-2">
                {variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => handleSelectVariant(variant)}
                    data-testid={`variant-option-${variant.id}`}
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-amber-100 hover:border-primary hover:bg-amber-50/60 transition-all text-left group"
                  >
                    <div>
                      <p className="font-semibold text-accent text-sm group-hover:text-primary transition-colors">{variant.name}</p>
                      {variant.selection_groups?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Choose {variant.selection_groups.length} item{variant.selection_groups.length !== 1 ? 's' : ''} + greeting card
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-amber-400 group-hover:text-primary transition-colors flex-shrink-0 ml-3" />
                  </button>
                ))}
              </div>
              {/* Representative image disclaimer */}
              <div className="mt-5 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="hamper-image-disclaimer">
                <Gift className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  The gift hamper image is only representative. Once the order is placed, our team will reach out to understand your customisation requirements.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP 2: Visual slot picker ── */}
          {step === 2 && selectedVariant && (
            <div data-testid="step-item-selection">
              {itemGroups.length === 0 ? (
                <div className="py-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <Check className="h-6 w-6 text-primary" />
                  </div>
                  <p className="font-semibold text-accent mb-1">{selectedVariant.name}</p>
                  <p className="text-sm text-muted-foreground">Fixed contents — now choose your greeting card below.</p>
                </div>
              ) : (
                <>
                  {/* Progress */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-accent text-sm">Customise Your Hamper</h3>
                      <p className="text-xs text-muted-foreground">{filledCount}/{totalSlots} slots filled</p>
                    </div>
                    {allItemsSelected && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                        <Check className="h-3 w-3 mr-1" /> All Set!
                      </Badge>
                    )}
                  </div>

                  {/* Slot bar (items + greeting card) */}
                  <SlotBar
                    itemGroups={itemGroups}
                    selections={itemSelections}
                    activeSlotIdx={activeSlotIdx}
                    onSlotClick={setActiveSlotIdx}
                    greetingCard={greetingCard}
                  />
                </>
              )}

              {/* ── Product item picker ── */}
              {!isGreetingSlotActive && activeGroup && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-[10px] font-bold">{activeSlotIdx + 1}</span>
                    </div>
                    <h4 className="font-semibold text-accent text-sm">{activeGroup.label}</h4>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`item-group-${activeGroup.id}`}>
                    {activeGroup.options.map(option => (
                      <ItemCard
                        key={option.id}
                        option={option}
                        isSelected={itemSelections[activeGroup.id] === option.id}
                        onSelect={() => handleItemSelect(activeGroup.id, option.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* ── Greeting card picker ── */}
              {isGreetingSlotActive && (
                <div className="mt-4" data-testid="greeting-card-section">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-5 w-5 rounded-full bg-rose-400 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="h-3 w-3 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-accent text-sm">Add a Greeting Card</h4>
                      <p className="text-xs text-muted-foreground">Pick an occasion or skip</p>
                    </div>
                  </div>

                  {/* Occasion grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="occasion-grid">
                    {GREETING_CARD_OPTIONS.map(option => (
                      <OccasionCard
                        key={option.id}
                        option={option}
                        isSelected={greetingCard.occasion === option.id}
                        onSelect={() => handleOccasionSelect(option.id)}
                      />
                    ))}
                  </div>

                  {/* Message customisation (shown when a real occasion is selected) */}
                  {greetingCard.occasion && greetingCard.occasion !== 'no-card' && (
                    <div className="mt-4" data-testid="greeting-message-section">
                      <div className="flex items-center gap-1.5 mb-2">
                        <MessageSquare className="h-4 w-4 text-rose-400" />
                        <label className="text-sm font-medium text-accent">Your personalised message</label>
                      </div>
                      <Textarea
                        placeholder={`Write your ${GREETING_CARD_OPTIONS.find(o => o.id === greetingCard.occasion)?.name ?? ''} message here…`}
                        value={greetingCard.message}
                        onChange={e => setGreetingCard(prev => ({ ...prev, message: e.target.value }))}
                        maxLength={200}
                        rows={3}
                        className="resize-none border-rose-200 focus:border-rose-400 focus:ring-rose-200 text-sm"
                        data-testid="greeting-message-input"
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-muted-foreground">Optional — leave blank for a plain card</p>
                        <span className="text-xs text-muted-foreground">{greetingCard.message.length}/200</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Price summary bar */}
              <div className={`rounded-xl p-4 border-2 mt-5 transition-all ${allItemsSelected ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{allItemsSelected ? 'Total Price' : 'Base Price'}</p>
                    <p className="text-2xl font-bold text-primary" data-testid="hamper-total-price">₹{totalPrice}</p>
                    {!allItemsSelected && itemGroups.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {filledCount < totalSlots ? `${totalSlots - filledCount} slot${totalSlots - filledCount > 1 ? 's' : ''} remaining` : ''}
                      </p>
                    )}
                  </div>
                  {allItemsSelected && itemGroups.length > 0 && greetingCard.occasion && greetingCard.occasion !== 'no-card' && (
                    <div className="text-right max-w-[45%]">
                      <div className="flex items-center gap-1 justify-end mb-1">
                        <CreditCard className="h-3 w-3 text-rose-400" />
                        <p className="text-xs font-medium text-rose-600">
                          {GREETING_CARD_OPTIONS.find(o => o.id === greetingCard.occasion)?.name} Card
                        </p>
                      </div>
                      {greetingCard.message && (
                        <p className="text-xs text-muted-foreground italic line-clamp-2">"{greetingCard.message}"</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="px-4 sm:px-5 pb-5 flex gap-3 sticky bottom-0 bg-white border-t border-gray-100 pt-4">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : () => { setStep(1); setSelectedVariant(null); setItemSelections({}); setActiveSlotIdx(0); setGreetingCard({ occasion: null, message: '' }); }}
            className="flex-1 border-amber-200"
            data-testid="hamper-back-button"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 1 ? 'Back' : 'Change Variant'}
          </Button>
          {step === 2 && (
            <Button
              onClick={handleSubmit}
              disabled={!allItemsSelected || saving}
              className="flex-1 bg-primary hover:bg-primary/90"
              data-testid={editMode ? 'hamper-update-button' : 'hamper-add-to-cart-button'}
            >
              {editMode
                ? <><RefreshCw className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />{saving ? 'Updating…' : 'Update Hamper'}</>
                : <><ShoppingCart className="h-4 w-4 mr-2" />{saving ? 'Adding…' : `Add to Cart — ₹${totalPrice}`}</>
              }
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
